/**
 * HistoricalChart Component
 *
 * Displays historical revenue and PROA target trends for a specific client over the last 3 years.
 * Uses Recharts library for interactive, responsive visualization with accessibility support.
 *
 * @module components/charts/HistoricalChart
 */

import React, { useMemo } from 'react'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts'
import { formatCurrency } from '@/lib/services/sig-data.service'

// ============================================================================
// TYPES
// ============================================================================

export interface HistoricalDataPoint {
  period: string // e.g., "Jan/2023", "2023"
  actualRevenue: number
  proaTarget: number
  variance: number
  variancePercent: number
}

export interface HistoricalChartProps {
  clientCode: string
  selectedYear: number
  className?: string
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    value: number
    dataKey: string
    color: string
    payload: HistoricalDataPoint
  }>
  label?: string
}

// ============================================================================
// CUSTOM HOOK FOR HISTORICAL DATA FETCHING
// ============================================================================

/**
 * Custom hook to fetch and transform 3-year historical data for a client
 *
 * @param clientCode - Client identifier
 * @param selectedYear - Currently selected year (used for context, fetches 3 years)
 * @returns Historical data state with loading, error, and data
 */
const useHistoricalData = (clientCode: string, selectedYear: number) => {
  const [state, setState] = React.useState<{
    loading: boolean
    error: Error | null
    data: HistoricalDataPoint[]
  }>({
    loading: true,
    error: null,
    data: [],
  })

  React.useEffect(() => {
    const fetchHistoricalData = async () => {
      setState({ loading: true, error: null, data: [] })

      try {
        // Simulate async API call with small delay
        await new Promise((resolve) => setTimeout(resolve, 100))

        // For now, we'll use mock data that simulates 3 years of monthly historical data
        // In production, this would call:
        // - getClientMonthly() for actual revenue from SIG API
        // - getClientBudget() for PROA targets from budgets API
        // - Combine data for last 3 years

        // Calculate the 3-year range centered on selectedYear
        const years = [selectedYear - 1, selectedYear, selectedYear + 1]

        // Mock data structure - in production, fetch from API
        const mockData: HistoricalDataPoint[] = []

        years.forEach((year, yearIndex) => {
          // Generate 12 months of data for each year
          const months = [
            'Jan',
            'Fev',
            'Mar',
            'Abr',
            'Mai',
            'Jun',
            'Jul',
            'Ago',
            'Set',
            'Out',
            'Nov',
            'Dez',
          ]

          months.forEach((month, monthIndex) => {
            // Simulate realistic revenue growth pattern
            const baseRevenue = 1000000 + yearIndex * 200000 // Year-over-year growth
            const monthlyVariation = Math.sin(monthIndex / 2) * 150000 // Seasonal variation
            const actualRevenue = baseRevenue + monthlyVariation + Math.random() * 100000

            // PROA target is typically 10-15% higher than actual
            const proaTarget = actualRevenue * (1.1 + Math.random() * 0.05)

            // Round values first, then calculate variance to avoid rounding errors
            const roundedActual = Math.round(actualRevenue)
            const roundedProa = Math.round(proaTarget)
            const variance = roundedActual - roundedProa
            const variancePercent = (variance / roundedProa) * 100

            mockData.push({
              period: `${month}/${year}`,
              actualRevenue: roundedActual,
              proaTarget: roundedProa,
              variance,
              variancePercent: Math.round(variancePercent * 100) / 100,
            })
          })
        })

        setState({ loading: false, error: null, data: mockData })
      } catch (error) {
        // Error state will be handled by component rendering
        setState({
          loading: false,
          error: error instanceof Error ? error : new Error('Unknown error'),
          data: [],
        })
      }
    }

    fetchHistoricalData()
  }, [clientCode, selectedYear])

  return state
}

// ============================================================================
// CUSTOM TOOLTIP COMPONENT
// ============================================================================

/**
 * Custom tooltip component for displaying detailed information on hover
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null
  }

  const data = payload[0].payload as HistoricalDataPoint

  return (
    <div className="bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
      <p className="font-semibold mb-2 text-gray-900 dark:text-gray-100">{label}</p>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span className="text-gray-600 dark:text-gray-400">Realizado:</span>
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatCurrency(data.actualRevenue)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-gray-600 dark:text-gray-400">PROA Target:</span>
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatCurrency(data.proaTarget)}
          </span>
        </div>
        <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600 dark:text-gray-400">Variação:</span>
            <span
              className={`font-semibold ${
                data.variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}
            >
              {formatCurrency(data.variance)} ({data.variancePercent > 0 ? '+' : ''}
              {data.variancePercent}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// ACCESSIBLE DATA TABLE COMPONENT
// ============================================================================

/**
 * Hidden data table for screen reader accessibility
 */
const HistoricalDataTable: React.FC<{ data: HistoricalDataPoint[] }> = ({ data }) => {
  return (
    <table
      className="sr-only"
      aria-label="Historical revenue and PROA target data table"
    >
      <thead>
        <tr>
          <th>Período</th>
          <th>Receita Realizada</th>
          <th>PROA Target</th>
          <th>Variação</th>
        </tr>
      </thead>
      <tbody>
        {data.map((point, index) => (
          <tr key={index}>
            <td>{point.period}</td>
            <td>{formatCurrency(point.actualRevenue)}</td>
            <td>{formatCurrency(point.proaTarget)}</td>
            <td>
              {formatCurrency(point.variance)} ({point.variancePercent}%)
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

const EmptyState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center">
      <svg
        className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        Sem dados históricos
      </h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm">
        Não há dados históricos disponíveis para este cliente.
      </p>
    </div>
  )
}

// ============================================================================
// LOADING STATE COMPONENT
// ============================================================================

const LoadingState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-96">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"
        role="status"
        aria-label="Loading"
      ></div>
      <p className="text-gray-500 dark:text-gray-400">Carregando dados históricos...</p>
    </div>
  )
}

// ============================================================================
// ERROR STATE COMPONENT
// ============================================================================

const ErrorState: React.FC<{ error: Error; onRetry?: () => void }> = ({ error, onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center">
      <svg
        className="w-16 h-16 text-red-500 dark:text-red-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        Erro ao carregar dados
      </h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-4">{error.message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
        >
          Tentar novamente
        </button>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * HistoricalChart - Displays 3-year historical revenue vs PROA target trends
 *
 * @param props.clientCode - Client identifier
 * @param props.selectedYear - Currently selected year for context
 * @param props.className - Optional CSS classes
 */
export const HistoricalChart = React.memo<HistoricalChartProps>(
  ({ clientCode, selectedYear, className = '' }) => {
    const { loading, error, data } = useHistoricalData(clientCode, selectedYear)

    // Memoize the area fill gradient calculation
    const areaFillColor = useMemo(() => {
      if (data.length === 0) return '#D1FAE5'

      // Calculate average variance to determine overall trend color
      const avgVariance = data.reduce((sum, point) => sum + point.variance, 0) / data.length

      return avgVariance >= 0 ? '#D1FAE5' : '#FEE2E2' // green-100 : red-100
    }, [data])

    // Format Y-axis currency values
    const formatYAxis = (value: number) => {
      if (value >= 1000000) {
        return `R$ ${(value / 1000000).toFixed(1)}M`
      }
      if (value >= 1000) {
        return `R$ ${(value / 1000).toFixed(0)}K`
      }
      return formatCurrency(value)
    }

    // Render loading state
    if (loading) {
      return <LoadingState />
    }

    // Render error state
    if (error) {
      return <ErrorState error={error} />
    }

    // Render empty state
    if (data.length === 0) {
      return <EmptyState />
    }

    return (
      <div className={`w-full ${className}`} role="img" aria-label={`Historical revenue and PROA target chart for client ${clientCode}`}>
        {/* Accessible data table for screen readers */}
        <HistoricalDataTable data={data} />

        {/* Responsive chart container */}
        <div className="w-full h-96 md:h-[500px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            {/* Grid */}
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />

            {/* X-Axis: Time periods */}
            <XAxis
              dataKey="period"
              stroke="#6B7280"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />

            {/* Y-Axis: Currency values */}
            <YAxis
              stroke="#6B7280"
              tick={{ fontSize: 12 }}
              tickFormatter={formatYAxis}
              width={80}
            />

            {/* Custom tooltip */}
            <Tooltip content={<CustomTooltip />} />

            {/* Legend */}
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
              formatter={(value) => {
                if (value === 'actualRevenue') return 'Realizado (Receita Atual)'
                if (value === 'proaTarget') return 'PROA (Meta)'
                return value
              }}
            />

            {/* Area fill between lines (variance visualization) */}
            <Area
              type="monotone"
              dataKey="proaTarget"
              fill={areaFillColor}
              fillOpacity={0.2}
              stroke="none"
            />

            {/* PROA Target line (dashed green) */}
            <Line
              type="monotone"
              dataKey="proaTarget"
              stroke="#10B981"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 3, fill: '#10B981' }}
              activeDot={{ r: 5 }}
              name="PROA Target"
            />

            {/* Actual Revenue line (solid blue) */}
            <Line
              type="monotone"
              dataKey="actualRevenue"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ r: 3, fill: '#3B82F6' }}
              activeDot={{ r: 5 }}
              name="Actual Revenue"
            />
          </ComposedChart>
        </ResponsiveContainer>
        </div>
      </div>
    )
  }
)

HistoricalChart.displayName = 'HistoricalChart'
